'use client';

import { createRoute } from '@tanstack/react-router';
import { Suspense, useEffect, useState } from 'react';

import MarketingLayout from '@/components/layouts/MarketingLayout';
import { Skeleton } from '@/components/ui';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  head: () => seoHead('/terms'),
  component: Terms,
});

const sections = [
  {
    heading: '1. Agreement to terms',
    body: 'By accessing or using Mindrealm, you agree to these Terms of Service. If you are using Mindrealm on behalf of an organization, you agree to these terms on its behalf and represent that you have authority to do so.',
  },
  {
    heading: '2. The service',
    body: 'Mindrealm is a hosted automated code-review service. It analyzes source code you connect to it and returns findings. Features may change over time as the product evolves.',
  },
  {
    heading: '3. Accounts',
    body: 'You must provide accurate account information and keep your credentials secure. You are responsible for activity under your account and for your team members you invite.',
  },
  {
    heading: '4. Acceptable use',
    body: 'You may not use Mindrealm to violate the law, infringe others’ rights, attempt to access code or accounts you are not authorized to access, or interfere with the operation of the service. You are responsible for ensuring you have the right to submit any code you connect.',
  },
  {
    heading: '5. Your code and content',
    body: 'You retain all rights to your code. You grant Mindrealm a limited license to access, copy, and process your code solely to provide the review service to you. We do not use your code to train models, and we do not share it except as needed to operate the service (see the Privacy Policy).',
  },
  {
    heading: '6. Fees and billing',
    body: 'Mindrealm is billed per seat, per month. New subscriptions include a free trial; after the trial, the subscription renews automatically until canceled. Payments are processed by our payment provider. Fees are non-refundable except where required by law.',
  },
  {
    heading: '7. Termination',
    body: 'You may cancel at any time. We may suspend or terminate access for violation of these terms or to comply with the law. On termination, your right to use the service ends.',
  },
  {
    heading: '8. Disclaimers',
    body: 'Mindrealm is provided "as is." It is a tool to assist code review and does not guarantee that code is free of defects, vulnerabilities, or other issues. You remain responsible for your code and for decisions you make based on its findings.',
  },
  {
    heading: '9. Limitation of liability',
    body: 'To the maximum extent permitted by law, Mindrealm and its operators are not liable for indirect, incidental, or consequential damages, or for any loss arising from your use of or reliance on the service.',
  },
  {
    heading: '10. Changes',
    body: 'We may update these terms from time to time. Continued use of the service after changes take effect constitutes acceptance of the updated terms.',
  },
  {
    heading: '11. Contact',
    body: 'Questions about these terms can be sent to legal@mindrealm.ai.',
  },
];

function TermsContent() {
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const showLoading = !mounted;

  if (showLoading) {
    return (
      <MarketingLayout isLoading={true} isSignedIn={false}>
        <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
          <div className="mx-auto max-w-[760px]">
            <Skeleton className="mb-8 h-12 w-[40%]" />
            <Skeleton className="h-96 w-full rounded-xl" />
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout isLoading={false} isSignedIn={false}>
      <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
        <div
          className="mx-auto max-w-[760px]"
          style={{ fontFamily: theme.fonts.body }}
        >
          <h1
            className="text-gradient-hero mb-2 text-4xl font-bold tracking-tight md:text-5xl"
            style={{ fontFamily: theme.fonts.display, lineHeight: 1.2 }}
          >
            Terms of Service
          </h1>
          <p className="text-foreground/60 mb-6 text-sm">
            Last updated: June 2, 2026.
          </p>

          <div className="space-y-8">
            {sections.map((section) => (
              <div key={section.heading}>
                <h2
                  className="mb-2 text-xl font-semibold"
                  style={{ fontFamily: theme.fonts.display }}
                >
                  {section.heading}
                </h2>
                <p className="text-foreground/80">{section.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Terms() {
  return (
    <Suspense
      fallback={
        <MarketingLayout isLoading={true} isSignedIn={false}>
          <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
            <div className="mx-auto max-w-[760px]">
              <Skeleton className="mb-8 h-12 w-[40%]" />
              <Skeleton className="h-96 w-full rounded-xl" />
            </div>
          </section>
        </MarketingLayout>
      }
    >
      <TermsContent />
    </Suspense>
  );
}
